import { m } from 'framer-motion';
import { SectionHeading } from './SectionHeading';
import { RegistrationMark } from './Ornaments';

const experiences = [
  {
    company: "nullplatform",
    role: "Software Developer",
    logo: "/logos/nullplatform_logo.jpg",
    period: "2023 — Present",
  },
  {
    company: "Strike",
    role: "Front-End Developer",
    logo: "/logos/strike_sh_logo.jpeg",
    period: "2023",
  },
  {
    company: "Menta Tickets",
    role: "Lead Front-End Developer",
    logo: "/logos/mentatickets_logo.jpeg",
    period: "2022 — 2023",
  },
  {
    company: "COR",
    role: "Front-End Developer",
    logo: "/logos/cor_logo.jpeg",
    period: "2021 — 2022",
  },
  {
    company: "Mercado Libre",
    role: "Front-End Developer",
    logo: "/logos/mercadolibre_logo.jpeg",
    period: "2019 — 2021",
  },
  {
    company: "BP4",
    role: "Front-End Developer",
    logo: "/logos/bp4_logo.jpeg",
    period: "2018 — 2019",
  },
  {
    company: "Deloitte Argentina",
    role: "Front-End Developer",
    logo: "/logos/deloitte_logo.jpeg",
    period: "2014 — 2018",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 }
  },
};

export function Experience({ isDark = true }) {
  // The list gets -mx-3 to cancel each row's px-3, so the logos align with
  // the title and the prose on a single left edge. The hover wash still
  // bleeds 12px past the text column on both sides, which is the point.
  return (
    <m.section
      id="experience"
      className="mb-20 sm:mb-24"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <SectionHeading index="02" title="experience" isDark={isDark} />

      <m.div
        className="-mx-3 space-y-1"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
      >
        {experiences.map((exp, index) => (
          <m.div
            key={exp.company}
            variants={itemVariants}
            className={`group relative flex items-center gap-4 rounded-2xl px-3 py-3.5 transition-colors duration-300 ${
              isDark ? 'hover:bg-white/[0.028]' : 'hover:bg-black/[0.028]'
            }`}
          >
            {/* Registration mark: a printer's plate cross, only on hover */}
            <span
              className={`pointer-events-none absolute right-3 top-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${
                isDark ? 'text-neutral-700' : 'text-neutral-300'
              }`}
            >
              <RegistrationMark className="h-2 w-2" />
            </span>

            <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-[11px]">
              {exp.logo ? (
                <img
                  src={exp.logo}
                  alt={`${exp.company} logo`}
                  width={40}
                  height={40}
                  /* The top rows sit at or near the fold; deferring them just
                     delays their paint for no bandwidth win at 40px. */
                  loading={index < 3 ? 'eager' : 'lazy'}
                  decoding="async"
                  className={`h-full w-full object-cover transition-all duration-500 ${
                    isDark
                      ? 'opacity-[0.72] saturate-[0.85] group-hover:opacity-100 group-hover:saturate-100'
                      : 'opacity-80 saturate-[0.9] group-hover:opacity-100 group-hover:saturate-100'
                  }`}
                />
              ) : (
                <span
                  className={`flex h-full w-full items-center justify-center font-display text-lg ${
                    isDark ? 'bg-white/5 text-neutral-400' : 'bg-black/5 text-neutral-600'
                  }`}
                >
                  {exp.company.charAt(0)}
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              {/* Proper noun: real case, sans medium. Not a heading voice. */}
              <h3
                className={`text-[0.9375rem] font-medium leading-snug tracking-[-0.006em] transition-colors ${
                  isDark
                    ? 'text-neutral-300 group-hover:text-neutral-50'
                    : 'text-neutral-800 group-hover:text-black'
                }`}
              >
                {exp.company}
              </h3>
              {/* Role + period share a line: role reads, period is data */}
              <div className="mt-0.5 flex items-baseline gap-2">
                <p
                  className={`truncate text-[0.8125rem] leading-snug transition-colors ${
                    isDark ? 'text-neutral-500' : 'text-neutral-500'
                  }`}
                >
                  {exp.role}
                </p>
                <span
                  className={`u-label ml-auto flex-shrink-0 transition-colors ${
                    isDark
                      ? 'text-neutral-700 group-hover:text-neutral-500'
                      : 'text-neutral-400 group-hover:text-neutral-500'
                  }`}
                >
                  {exp.period}
                </span>
              </div>
            </div>
          </m.div>
        ))}
      </m.div>
    </m.section>
  );
}
